import { HttpClient, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConsultaPaginada, ConsultaPaginadaResultado } from 'maestro-ng';
import { ConversorMonitoramento } from '../monitoramento/conversor-monitoramento.model';
import { ExtratorMonitoramento } from '../monitoramento/extrator-monitoramento.model';
import { IrisMonitoramento } from '../monitoramento/iris-monitoramento.model';
import { PrismaMonitoramento } from '../monitoramento/prisma-monitoramento.model';
import { BaseService } from '../services/base-service.service';
import { FonteDadosProxyRotaAbstract } from './fonte-dados-proxy/fonte-dados-proxy-rota-abstract.model';
import { FonteDados } from './fonte-dados.model';
import { PessoaProcessamentoMetadados } from './pessoa-processamento-metadados.model';
import { ProcessoDocumentoProcessamentoMetadados } from './processo-documento-processamento-metadados.model';
import { ProcessoProcessamentoMetadados } from './processo-processamento-metadados.model copy';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { AplicacaoK8s } from './aplicacao-k8s.model';
import { PodK8s } from './pod-k8s.model';
import { FonteDadosBandoDeDados } from './fonte-dados-banco-de-dados/fonte-dados-banco-de-dados.model';
import { AppService } from '../app.service';

@Injectable()
export class FonteDadosService extends BaseService<FonteDados, number> {
  constructor(private appService: AppService, http: HttpClient) {
    super('fonteDados', http);
  }

  public recuperarPorOrgaoJusticaId(orgaoJusticaId: number) {
    return firstValueFrom(this.http.get<FonteDados[]>(this.path(`recuperarPorOrgaoJusticaId/${orgaoJusticaId}`)));
  }

  public monitorarConversor(fonteDados: FonteDados): Promise<ConversorMonitoramento> {
    return firstValueFrom(this.http.get<ConversorMonitoramento>(this.path(`monitorarConversor/${fonteDados.id}`)));
  }

  public monitorarExtrator(fonteDados: FonteDados): Promise<ExtratorMonitoramento> {
    return firstValueFrom(this.http.get<ExtratorMonitoramento>(this.path(`monitorarExtrator/${fonteDados.id}`)));
  }

  public monitorarIris(fonteDados: FonteDados): Promise<IrisMonitoramento> {
    return firstValueFrom(this.http.get<IrisMonitoramento>(this.path(`monitorarIris/${fonteDados.id}`)));
  }

  public monitorarPrisma(fonteDados: FonteDados): Promise<PrismaMonitoramento> {
    return firstValueFrom(this.http.get<PrismaMonitoramento>(this.path(`monitorarPrisma/${fonteDados.id}`)));
  }

  public recuperarPessoasProcessamentosMetadados(
    fonteDados: FonteDados,
    consulta: ConsultaPaginada
  ): Promise<ConsultaPaginadaResultado<PessoaProcessamentoMetadados>> {
    const url = this.path(`recuperarPessoasProcessamentosMetadados/${fonteDados.id}`);
    return firstValueFrom(this.http.post<ConsultaPaginadaResultado<PessoaProcessamentoMetadados>>(url, consulta));
  }

  public recuperarProcessosProcessamentosMetadados(
    fonteDados: FonteDados,
    consulta: ConsultaPaginada
  ): Promise<ConsultaPaginadaResultado<ProcessoProcessamentoMetadados>> {
    const url = this.path(`recuperarProcessosProcessamentosMetadados/${fonteDados.id}`);
    return firstValueFrom(this.http.post<ConsultaPaginadaResultado<ProcessoProcessamentoMetadados>>(url, consulta));
  }

  public recuperarProcessosDocumentosProcessamentosMetadados(
    fonteDados: FonteDados,
    consulta: ConsultaPaginada
  ): Promise<ConsultaPaginadaResultado<ProcessoDocumentoProcessamentoMetadados>> {
    const url = this.path(`recuperarProcessosDocumentosProcessamentosMetadados/${fonteDados.id}`);
    return firstValueFrom(this.http.post<ConsultaPaginadaResultado<ProcessoDocumentoProcessamentoMetadados>>(url, consulta));
  }

  public recuperarListaAlteracoesBancoDeDados(
    fonteDados: FonteDados,
    consulta: ConsultaPaginada
  ): Promise<ConsultaPaginadaResultado<FonteDadosBandoDeDados>> {
    const url = this.path(`${fonteDados.nome}/extrator/recuperarBancoDadosRegistroAlteracao`);
    return firstValueFrom(this.http.post<ConsultaPaginadaResultado<FonteDadosBandoDeDados>>(url, consulta));
  }

  public recuperarPorNome(nome: string): Promise<FonteDados> {
    return firstValueFrom(this.http.get<FonteDados>(this.path(`recuperarPorNome/${nome}`)));
  }

  public fonteDadosExtrator(fonteDadosNome: string, fonteDadosProxy: FonteDadosProxyRotaAbstract): Promise<any> {
    const request = new HttpRequest(
      fonteDadosProxy.metodo,
      this.path(`${fonteDadosNome}/extrator/${fonteDadosProxy.path()}`),
      fonteDadosProxy.body(),
      {
        params: fonteDadosProxy.query(),
        responseType: fonteDadosProxy.tipoResposta,
      }
    );

    return lastValueFrom(this.http.request<any>(request));
  }

  public recuperarPaginado(consulta: ConsultaPaginada): Promise<ConsultaPaginadaResultado<FonteDados>> {
    return this.service.recuperarPaginado(consulta);
  }

  public recuperarSugestoes(termo: string): Promise<FonteDados[]> {
    return this.service.recuperarSugestoes(termo);
  }

  public recuperarItens(): Promise<FonteDados[]> {
    return this.service.recuperarItens();
  }

  public excluir(fonteDados: FonteDados): Promise<void> {
    return this.service.excluirPorId(fonteDados.id);
  }

  public recuperarPorId(id: number): Promise<FonteDados> {
    return this.service.recuperarPorId(id);
  }

  public salvar(fonteDados: FonteDados): Promise<FonteDados> {
    return this.service.salvar(fonteDados, fonteDados.id);
  }

  public recuperarAplicacoes(fonteDados: FonteDados): Observable<AplicacaoK8s[]> {
    return this.http.get<AplicacaoK8s[]>(this.path(`${fonteDados.id}/recuperarPods`));
  }

  public recuperarAplicacao(fonteDados: FonteDados, aplicacaoNome: string): Observable<AplicacaoK8s> {
    return this.http.get<AplicacaoK8s>(this.path(`${fonteDados.id}/recuperarPods/${aplicacaoNome.toUpperCase()}`));
  }

  public recuperarPodLogs(fonteDados: FonteDados, pod: PodK8s, containerNome?: string, numLinhas?: number): Observable<string> {
    let params = new HttpParams();
    if (numLinhas) {
      params = params.append('numLinhas', String(numLinhas));
    }

    containerNome = containerNome || pod.containers[0].nome;
    return this.http.get(this.path(`${fonteDados.id}/recuperarPodLogs/${pod.aplicacao}/${pod.nome}/${containerNome}`), {
      responseType: 'text' as const,
      params: params,
    });
  }

  public reiniciarPod(fonteDados: FonteDados, pod: PodK8s): Observable<any> {
    return this.http.delete(this.path(`${fonteDados.id}/reiniciarPod/${pod.aplicacao}/${pod.nome}`));
  }

  public atualizarVersao(fonteDados: FonteDados, aplicacao: AplicacaoK8s, novaVersao: string): Observable<void> {
    return this.http.post<void>(this.path(`${fonteDados.id}/atualizarVersao/${aplicacao.aplicacao}`), novaVersao, {
      headers: this.createJsonHttpHeaders(),
    });
  }
}
